<template>
    <div class="EvieChatView">
        <EvieChat
            :conversation-summaries="conversationSummaries"
            :active-conversation="activeConversation"
            :active-conversation-id="activeConversationId"
            :send-status="sendStatus"
            :input-draft="inputDraft"
            :is-composing="isComposing"
            :is-loading="loadingConversation"
            :rp-name="rpName"
            :streaming-message="streamingMessage"
            :streaming-conversation-ids="streamingConversationIds"
            @send="onSend"
            @cancel="onCancel"
            @new-chat="onNewChat"
            @retry="onRetry"
            @draft-change="onDraftChange"
            @select-conversation="onSelectConversation"
            @feedback="onFeedback"
        />
    </div>
</template>

<style lang="scss">
.EvieChatView {
    flex: 1;
    min-width: 0;
    height: 100%;
    overflow: hidden;
}
</style>

<script lang="ts">
    import { defineComponent } from 'vue';

    import { noop } from 'lodash';

    import { EvieConversationSummary } from '@evidentid/tprm-portal-lib/models/evie/EvieConversationSummary.model';

    import { EvieConversation } from '@/modules/evie-ai/models/EvieConversation.model';
    import { OPTIMISTIC_CONVERSATION_ID, useEvieAiStore } from '@/modules/evie-ai/store';
    import { clearDraft, loadDraft, saveDraft } from '@/modules/evie-ai/utils/draftStorage';

    import EvieChat from '../components/EvieChat/EvieChat.vue';
    import { useUserStore } from '@evidentid/dashboard-commons/modules/auth/store/user';

    export default defineComponent({
        name: 'EvieChatView',
        components: { EvieChat },
        data() {
            return {
                isComposing: false,
                composingDraft: '',
            };
        },
        computed: {
            rpName(): string {
                return (this as any).$rp?.current || '';
            },
            userEmail(): string {
                return useUserStore().data?.email || '';
            },
            conversationSummaries(): Record<string, EvieConversationSummary> {
                return useEvieAiStore().conversationSummaries;
            },
            activeConversation(): EvieConversation | null {
                return useEvieAiStore().activeConversation;
            },
            activeConversationId(): string | null {
                return useEvieAiStore().activeConversationId;
            },
            sendStatus(): 'idle' | 'loading' {
                const storeStatus = useEvieAiStore().sendStatus as 'idle' | 'loading';
                if (storeStatus === 'loading') {
                    return 'loading'; // optimistic conversation creation in progress
                }
                const streamingMessages = useEvieAiStore().streamingMessages as Record<string, string>;
                return this.activeConversationId && this.activeConversationId in streamingMessages
                    ? 'loading'
                    : 'idle';
            },
            streamingConversationIds(): string[] {
                const streamingMessages = useEvieAiStore().streamingMessages as Record<string, string>;
                return Object.keys(streamingMessages);
            },
            inputDraft(): string {
                const routeConversationId = (this as any).$route?.params?.conversationId ?? null;
                if (routeConversationId && routeConversationId !== this.activeConversationId) {
                    return '';
                }
                if (!this.activeConversationId) {
                    return this.composingDraft;
                }
                return useEvieAiStore().activeConversation?.inputDraft ?? '';
            },
            streamingMessage(): string | null {
                const streamingMessages = useEvieAiStore().streamingMessages as Record<string, string>;
                return this.activeConversationId
                    ? (streamingMessages[this.activeConversationId] ?? null)
                    : null;
            },
            storeLoadingConversation(): boolean {
                return useEvieAiStore().loadingConversation;
            },
            loadingConversation(): boolean {
                const storeLoading = useEvieAiStore().loadingConversation;
                const loadingList = useEvieAiStore().loadingList;
                const routeConversationId = (this as any).$route?.params?.conversationId;
                const activeConversationId = useEvieAiStore().activeConversationId;
                const awaitingConversation = routeConversationId && activeConversationId !== routeConversationId;
                return storeLoading || loadingList || Boolean(awaitingConversation);
            },
        },
        watch: {
            rpName(newRpName: string) {
                if (!newRpName) {
                    return;
                }
                useEvieAiStore().clearAll();
                useEvieAiStore().loadConversationList({ rpName: newRpName });
                (this as any).$router.replace({
                    name: 'evieChat',
                    params: { rpId: newRpName },
                }).catch(noop);
            },
            storeLoadingConversation(loading: boolean, wasLoading: boolean) {
                if (wasLoading && !loading) {
                    const routeConversationId = (this as any).$route?.params?.conversationId;
                    const activeConversationId = useEvieAiStore().activeConversationId;
                    if (routeConversationId && !activeConversationId) {
                        (this as any).$router.replace({
                            name: 'evieChat',
                            params: { rpId: this.rpName },
                        }).catch(noop);
                    }
                }
            },
            '$route.params.conversationId': {
                immediate: true,
                handler(conversationId: string | undefined) {
                    if (conversationId && conversationId !== (this as any).activeConversationId) {
                        useEvieAiStore().switchToConversation({
                            rpName: (this as any).rpName,
                            conversationId,
                        });
                    } else if (!conversationId) {
                        useEvieAiStore().clearActiveConversation();
                    }
                },
            },
            activeConversationId(id: string | null) {
                if (id && id !== OPTIMISTIC_CONVERSATION_ID) {
                    const saved = loadDraft(this.userEmail, this.rpName, id);
                    if (saved) {
                        useEvieAiStore().setInputDraft({ draft: saved });
                    }
                    if (id !== (this as any).$route.params.conversationId) {
                        (this as any).$router.replace({
                            name: 'evieChat',
                            params: { rpId: this.rpName, conversationId: id },
                        }).catch(noop);
                    }
                } else if (!id) {
                    this.composingDraft = loadDraft(
                        this.userEmail,
                        this.rpName,
                        null,
                    );
                }
            },
        },
        created() {
            useEvieAiStore().loadConversationList({ rpName: this.rpName });
            if (!(this as any).$route?.params?.conversationId) {
                this.composingDraft = loadDraft(this.userEmail, this.rpName, null);
            }
        },
        methods: {
            onCancel() {
                useEvieAiStore().cancelStream({ rpName: this.rpName });
            },
            onSend(text: string) {
                const conversationId = this.activeConversationId;
                this.isComposing = false;
                clearDraft(this.userEmail, this.rpName, conversationId);
                if (!conversationId) {
                    this.composingDraft = '';
                }
                useEvieAiStore().sendMessage({ rpName: this.rpName, text });
            },
            onNewChat() {
                this.isComposing = true;
                (this as any).$router.push({
                    name: 'evieChat',
                    params: { rpId: this.rpName },
                }).catch(noop);
            },
            onRetry(feId: string) {
                useEvieAiStore().retryMessage({ rpName: this.rpName, feId });
            },
            onFeedback(payload: { feId: string, score: 'UP' | 'DOWN' }) {
                const conversationId = this.activeConversationId;
                if (!conversationId) {
                    return;
                }
                useEvieAiStore().submitFeedback({
                    rpName: this.rpName,
                    conversationId,
                    feId: payload.feId,
                    score: payload.score,
                });
            },
            onDraftChange(draft: string) {
                const conversationId = this.activeConversationId;
                if (conversationId && conversationId !== OPTIMISTIC_CONVERSATION_ID) {
                    useEvieAiStore().setInputDraft({ draft });
                    saveDraft(this.userEmail, this.rpName, conversationId, draft);
                } else if (!conversationId) {
                    this.composingDraft = draft;
                    saveDraft(this.userEmail, this.rpName, null, draft);
                }
            },
            onSelectConversation(conversationId: string) {
                (this as any).$router.push({
                    name: 'evieChat',
                    params: { rpId: this.rpName, conversationId },
                }).catch(noop);
            },
        },
    });
</script>
